// Browser Push Notification Service
import React from 'react';

class NotificationService {
    constructor() {
        this.permission = 'default';
        this.supported = 'Notification' in window;
    }

    async init() {
        if (!this.supported) {
            console.warn('Browser notifications not supported');
            return false;
        }

        this.permission = Notification.permission;
        
        if (this.permission === 'default') {
            this.permission = await Notification.requestPermission();
        }

        return this.permission === 'granted';
    }

    async requestPermission() {
        if (!this.supported) return false;
        
        try {
            this.permission = await Notification.requestPermission();
            return this.permission === 'granted';
        } catch (error) {
            console.error('Error requesting notification permission:', error);
            return false;
        }
    }

    isEnabled() {
        return this.supported && this.permission === 'granted';
    }

    show(title, options = {}) {
        if (!this.isEnabled()) {
            console.warn('Notifications not enabled');
            return null;
        }

        const defaultOptions = {
            icon: '/favicon.ico',
            badge: '/favicon.ico',
            vibrate: [200, 100, 200],
            tag: 'tabletop-looper',
            renotify: true,
            requireInteraction: false,
            ...options
        };

        try {
            const notification = new Notification(title, defaultOptions);
            
            notification.onclick = () => {
                window.focus();
                if (options.onClick) {
                    options.onClick();
                }
                notification.close();
            };

            // Auto-close after 10 seconds
            setTimeout(() => notification.close(), 10000);

            return notification;
        } catch (error) {
            console.error('Error showing notification:', error);
            return null;
        }
    }

    // Game-specific notifications
    showGameReminder(game) {
        return this.show(`Game Reminder: ${game.title}`, {
            body: `Your game "${game.game_name}" starts soon!`,
            tag: `reminder-${game.game_id}`,
            requireInteraction: true,
            data: { gameId: game.game_id, type: 'reminder' }
        });
    }

    showPlayerJoined(game, playerName) {
        return this.show(`Player Joined: ${game.title}`, {
            body: `${playerName} has joined your game`,
            tag: `player-joined-${game.game_id}`,
            data: { gameId: game.game_id, type: 'player_joined' }
        });
    }

    showNewMessage(game, senderName) {
        return this.show(`New Message in ${game.title}`, {
            body: `${senderName} sent a message`,
            tag: `message-${game.game_id}`,
            data: { gameId: game.game_id, type: 'message' }
        });
    }

    showGameStarting(game) {
        return this.show(`Game Starting: ${game.title}`, {
            body: `Your game is about to begin!`,
            tag: `starting-${game.game_id}`,
            requireInteraction: true,
            data: { gameId: game.game_id, type: 'game_starting' }
        });
    }
}

// Singleton instance
const notificationService = new NotificationService();

export default notificationService;

// React hook for notifications
export const useNotifications = () => {
    const [enabled, setEnabled] = React.useState(false);
    const [loading, setLoading] = React.useState(true);

    React.useEffect(() => {
        const init = async () => {
            const result = await notificationService.init();
            setEnabled(result);
            setLoading(false);
        };
        init();
    }, []);

    const requestPermission = async () => {
        setLoading(true);
        const result = await notificationService.requestPermission();
        setEnabled(result);
        setLoading(false);
        return result;
    };

    return {
        enabled,
        loading,
        supported: notificationService.supported,
        requestPermission,
        showNotification: notificationService.show.bind(notificationService),
        showGameReminder: notificationService.showGameReminder.bind(notificationService),
        showPlayerJoined: notificationService.showPlayerJoined.bind(notificationService),
        showNewMessage: notificationService.showNewMessage.bind(notificationService),
        showGameStarting: notificationService.showGameStarting.bind(notificationService),
    };
};
